/**
 * Spoken notes.
 *
 * Typing with gloves on in a cold mechanical room does not happen, so notes are dictated. Two
 * shapes, and the difference between them is the law:
 *
 *   - **At the curb**, sixty seconds before pulling out. Always available.
 *   - **On the drive**, armed while stationary and then hands-free for the whole trip. Quebec's
 *     Highway Safety Code prohibits handling a device while driving, so hands-free is the only
 *     lawful form of this feature — and that constraint is the design rather than an obstacle
 *     to it: nothing to look at, nothing to tap, a tone on start and a tone on stop.
 *
 * Two honest caveats, both from `FIELD.md`. Background capture is where a PWA is weakest —
 * Chrome on Android may suspend it when the screen locks and iOS will — so the recorder holds
 * a dimmed wake lock and the curb recording is the default rather than the bonus. And the
 * transcript is reviewed **on arrival, not while moving**, so a mis-heard part number is
 * caught by the person who said it.
 */

export type Recording = {
  blob: Blob;
  /** Seconds of audio kept, after the silence between sentences is dropped. */
  durationSeconds: number;
  /** Seconds the recorder was actually running, which on a drive is much longer. */
  elapsedSeconds: number;
};

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

function pickMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

export function recordingSupported(): boolean {
  return typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}

/**
 * A recorder that survives a drive.
 *
 * Holds a screen wake lock while running, dimmed by the caller's CSS: without it Android may
 * suspend capture the moment the screen locks, and the note the technician thought they were
 * dictating between two jobs would simply not exist.
 */
export class VoiceRecorder {
  private recorder?: MediaRecorder;
  private stream?: MediaStream;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private wakeLock: { release: () => Promise<void> } | null = null;
  /** Rough voice-activity estimate, so a 20-minute drive with 40 seconds of speech says 40. */
  private voicedMs = 0;
  private analyser?: AnalyserMode;

  async start(): Promise<void> {
    if (!recordingSupported()) throw new Error('unsupported');
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    const mimeType = pickMime();
    this.recorder = new MediaRecorder(this.stream, mimeType ? { mimeType, audioBitsPerSecond: 32_000 } : undefined);
    this.chunks = [];
    this.voicedMs = 0;
    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    this.recorder.start(1000);
    this.startedAt = Date.now();

    this.analyser = new AnalyserMode(this.stream, () => {
      this.voicedMs += 200;
    });

    // Best effort: a browser that refuses is a browser where the drive-time recorder is not
    // reliable, which the caller is expected to tell the technician rather than discover.
    try {
      this.wakeLock = (await (navigator as any).wakeLock?.request?.('screen')) ?? null;
    } catch {
      this.wakeLock = null;
    }
  }

  /** Whether the screen lock was actually granted. Drives the honest warning. */
  get wakeLockHeld(): boolean {
    return this.wakeLock !== null;
  }

  async stop(): Promise<Recording> {
    const recorder = this.recorder;
    if (!recorder) throw new Error('not_recording');

    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => resolve(new Blob(this.chunks, { type: recorder.mimeType || 'audio/webm' }));
      recorder.stop();
    });

    this.analyser?.stop();
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    await this.wakeLock?.release().catch(() => {});
    this.wakeLock = null;
    this.recorder = undefined;
    this.stream = undefined;

    const elapsedSeconds = Math.round((Date.now() - this.startedAt) / 1000);
    return {
      blob,
      // Rounded up to a second, and never larger than the elapsed time.
      durationSeconds: Math.min(elapsedSeconds, Math.max(1, Math.round(this.voicedMs / 1000))),
      elapsedSeconds,
    };
  }

  /** Abandon without keeping anything. */
  async cancel(): Promise<void> {
    this.analyser?.stop();
    try {
      this.recorder?.stop();
    } catch {
      /* already stopped */
    }
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    await this.wakeLock?.release().catch(() => {});
    this.wakeLock = null;
    this.recorder = undefined;
    this.stream = undefined;
  }
}

/**
 * How much of a recording is speech.
 *
 * A note dictated across a twenty-minute drive with forty seconds of speech in it should cost
 * forty seconds of storage and transcription. Trimming the audio itself needs a decode and a
 * re-encode the phone should not be doing while driving, so what is measured here is the
 * *duration reported*, and the server-side transcription is what skips the silence.
 */
class AnalyserMode {
  private timer?: number;
  private context?: AudioContext;

  constructor(stream: MediaStream, onVoiced: () => void) {
    try {
      this.context = new AudioContext();
      const source = this.context.createMediaStreamSource(stream);
      const analyser = this.context.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const buffer = new Uint8Array(analyser.frequencyBinCount);

      this.timer = window.setInterval(() => {
        analyser.getByteTimeDomainData(buffer);
        let peak = 0;
        for (const sample of buffer) peak = Math.max(peak, Math.abs(sample - 128));
        // A van is a noisy room; the threshold is well above road noise and well below speech.
        if (peak > 12) onVoiced();
      }, 200);
    } catch {
      // No analyser is not a failure: the recording still happens, and the duration is simply
      // the elapsed time.
    }
  }

  stop(): void {
    if (this.timer) window.clearInterval(this.timer);
    void this.context?.close().catch(() => {});
  }
}

/**
 * The tone that says the recorder is live.
 *
 * The whole audio feedback vocabulary, because there is nothing to look at: a rising pair on
 * start, a falling pair on stop. Synthesised rather than a file so it works with no network
 * and adds nothing to the bundle.
 */
export function tone(kind: 'start' | 'stop'): void {
  try {
    const context = new AudioContext();
    const now = context.currentTime;
    const [first, second] = kind === 'start' ? [660, 990] : [990, 660];
    for (const [index, frequency] of [first!, second!].entries()) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gain.gain.setValueAtTime(0.0001, now + index * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.2, now + index * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + index * 0.12 + 0.1);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(now + index * 0.12);
      oscillator.stop(now + index * 0.12 + 0.12);
    }
    window.setTimeout(() => void context.close().catch(() => {}), 600);
  } catch {
    // Audio feedback is a courtesy; its absence must never stop a recording.
  }
}
